
import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, IconButton, useTheme, Fade, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useAuth } from './AuthContext';

const SESSION_DURATION = 12 * 60 * 60 * 1000; // 12h
const WARNING_THRESHOLD = 1 * 60 * 60 * 1000; // 1h before

export default function SessionExpiryWarning(): React.ReactElement | null {
    const { loginTime, isAuthenticated, logout, login, user, softLoggedOut } = useAuth() as any;
    const [show, setShow] = useState<boolean>(false);
    const [isRenewing, setIsRenewing] = useState<boolean>(false);
    const [username, setUsername] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const theme = useTheme();

    // ユーザー名をあらかじめ入力しておく
    useEffect(() => {
        if (show && user?.username && !username) {
            setUsername(user.username);
        }
    }, [show, user, username]);

    // ソフトログアウト時はこのコンポーネントを非表示（モーダルのLoginFormに任せる）
    useEffect(() => {
        if (softLoggedOut) {
            setShow(false);
        }
    }, [softLoggedOut]);

    useEffect(() => {
        if (!isAuthenticated || !loginTime || softLoggedOut) {
            setShow(false);
            return;
        }

        const checkSession = () => {
            const start = new Date(loginTime).getTime();
            const now = Date.now();
            const elapsed = now - start;
            const remaining = SESSION_DURATION - elapsed;

            // 1時間前になったら表示
            if (remaining <= WARNING_THRESHOLD && remaining > 0) {
                setShow(true);
            } else if (remaining <= 0) {
                // セッション切れ
                logout();
            }
        };

        const timer = setInterval(checkSession, 30000); // 30秒ごとにチェック
        checkSession(); // 初回実行

        return () => clearInterval(timer);
    }, [isAuthenticated, loginTime, logout]);

    // グローバルな関数として登録し、App.js から呼び出せるようにする
    useEffect(() => {
        (window as any).showSessionRenewal = (forceRenew = false) => {
            setShow(true);
            if (forceRenew) setIsRenewing(true);
        };
        return () => { delete (window as any).showSessionRenewal; };
    }, []);

    if (!show) return null;

    const handleLogout = () => {
        logout();
        setShow(false);
    };

    const handleRenew = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!username || !password) {
            setError('ユーザー名とパスワードを入力してください');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await login(username, password);
            // 成功したらポップアップを閉じる
            setShow(false);
            setIsRenewing(false);
            setUsername('');
            setPassword('');
        } catch (err: any) {
            setError(err.message || 'ログインに失敗しました');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fade in={show}>
            <Paper
                elevation={6}
                sx={{
                    position: 'fixed',
                    top: 'calc(var(--app-header-height, 64px) + 8px)',
                    right: 20,
                    width: 320,
                    zIndex: 9999,
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.warning.light}`,
                    backgroundColor: '#fffdf5',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        セッション更新の確認
                    </Typography>
                    <IconButton size="small" onClick={() => setShow(false)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {!isRenewing ? (
                    <Box>
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.5 }}>
                            もうすぐログアウトします。<br />再ログインしてセッションを更新しますか？
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <Button size="small" variant="text" color="error" onClick={handleLogout}>
                                ログアウト
                            </Button>
                            <Button size="small" variant="outlined" color="inherit" onClick={() => setShow(false)}>
                                後で
                            </Button>
                            <Button size="small" variant="contained" color="primary" onClick={() => setIsRenewing(true)}>
                                はい
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleRenew}>
                        <Typography variant="caption" sx={{ mb: 1.5, display: 'block', color: 'text.primary', fontWeight: 500 }}>
                            セッションを更新するために再ログインしてください
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mb: 1, color: 'text.secondary' }}>
                            ユーザー: <strong>{user?.username}</strong>
                        </Typography>
                        <Box sx={{ mb: 1.5 }}>
                            <input
                                type="text"
                                placeholder="ユーザー名"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    fontSize: '13px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    marginBottom: '8px',
                                    outline: 'none'
                                }}
                            />
                            <input
                                type="password"
                                placeholder="パスワード"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    fontSize: '13px',
                                    borderRadius: '4px',
                                    border: '1px solid #ddd',
                                    outline: 'none'
                                }}
                            />
                        </Box>
                        {error && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1, lineHeight: 1.2 }}>
                                {error}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Button size="small" variant="text" color="error" onClick={handleLogout} sx={{ mr: 'auto' }}>
                                ログアウト
                            </Button>
                            <Button size="small" variant="text" color="inherit" onClick={() => setIsRenewing(false)} disabled={loading}>
                                戻る
                            </Button>
                            <Button
                                type="submit"
                                size="small"
                                variant="contained"
                                color="primary"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                            >
                                ログイン
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Fade>
    );
}
